import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './FlappyBird.css';

const FlappyBird = () => {
  const [birdY, setBirdY] = useState(200);
  const [birdVelocity, setBirdVelocity] = useState(0);
  const [pipes, setPipes] = useState([]);
  const [score, setScore] = useState(0);
  const [gameActive, setGameActive] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const animationRef = useRef(null);
  const pipeTimerRef = useRef(null);

  const GAME_HEIGHT = 500;
  const GAME_WIDTH = 400;
  const BIRD_SIZE = 30;
  const PIPE_WIDTH = 60;
  const PIPE_GAP = 150;
  const GRAVITY = 0.5;
  const JUMP_STRENGTH = -8;

  const startGame = () => {
    setGameActive(true);
    setGameOver(false);
    setBirdY(200);
    setBirdVelocity(0);
    setPipes([]);
    setScore(0);
  };

  const jump = useCallback(() => {
    if (gameActive && !gameOver) {
      setBirdVelocity(JUMP_STRENGTH);
    }
  }, [gameActive, gameOver, JUMP_STRENGTH]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      const key = e.key.toLowerCase();
      if (key === ' ' || key === 'w' || key === 'arrowup') {
        e.preventDefault();
        jump();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [jump]);

  useEffect(() => {
    if (!gameActive || gameOver) return;

    const gameLoop = () => {
      setBirdVelocity(v => v + GRAVITY);
      setBirdY(y => {
        const newY = y + birdVelocity;
        if (newY <= 0 || newY >= GAME_HEIGHT - BIRD_SIZE) {
          setGameActive(false);
          setGameOver(true);
          return y;
        }
        return newY;
      });

      setPipes(currentPipes => {
        const newPipes = currentPipes
          .map(pipe => ({ ...pipe, x: pipe.x - 3 }))
          .filter(pipe => pipe.x > -PIPE_WIDTH);

        newPipes.forEach(pipe => {
          if (!pipe.scored && pipe.x + PIPE_WIDTH < 50) {
            pipe.scored = true;
            setScore(s => s + 1);
          }

          const birdLeft = 50;
          const birdRight = birdLeft + BIRD_SIZE;
          const birdTop = birdY;
          const birdBottom = birdY + BIRD_SIZE;

          if (birdRight > pipe.x && birdLeft < pipe.x + PIPE_WIDTH) {
            if (birdTop < pipe.topHeight || birdBottom > pipe.topHeight + PIPE_GAP) {
              setGameActive(false);
              setGameOver(true);
            }
          }
        });

        return newPipes;
      });

      animationRef.current = requestAnimationFrame(gameLoop);
    };

    animationRef.current = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(animationRef.current);
  }, [gameActive, gameOver, birdY, birdVelocity]);

  useEffect(() => {
    if (!gameActive || gameOver) return;

    pipeTimerRef.current = setInterval(() => {
      const topHeight = Math.random() * (GAME_HEIGHT - PIPE_GAP - 100) + 50;
      setPipes(p => [...p, { x: GAME_WIDTH, topHeight, scored: false }]);
    }, 2000);

    return () => clearInterval(pipeTimerRef.current);
  }, [gameActive, gameOver]);

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">🐦 Flappy Bird</h1>
        <div className="game-instructions">
          Press SPACE, W, or ↑ to flap. Avoid the pipes!
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">Score: {score}</div>
        
        <div className="game-play-area">
          <div className="flappy-game-area" onClick={jump}>
            <div className="bird" style={{ top: birdY }}>
              🐦
            </div>
            
            {pipes.map((pipe, i) => (
              <React.Fragment key={i}>
                <div className="pipe pipe-top" style={{ left: pipe.x, height: pipe.topHeight }} />
                <div className="pipe pipe-bottom" style={{ left: pipe.x, top: pipe.topHeight + PIPE_GAP }} />
              </React.Fragment>
            ))}

            {!gameActive && (
              <div className="game-status-overlay">
                {gameOver ? (
                  <div className="status-message lose">💥 Game Over!</div>
                ) : (
                  <div className="status-message start">Click Start to Play!</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="game-controls">
          <button className="btn btn-primary" onClick={startGame}>
            {gameOver ? 'Play Again' : 'Start Game'}
          </button>
          <Link to="/" className="btn btn-secondary">Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default FlappyBird;
